import * as XLSX from "xlsx";

const EXPECTED_WEIGHBRIDGE_COLUMN_ORDER = [
  "weigh_bridge_ticket_number",
  "weigh_bridge_ticket_reference_no",
  "truck_licence_plate",
  "transporter_code",
  "transporter_name",
  "driver_contact_id",
  "truck_driver_name",
  "product_code",
  "product_name",
  "comments",
  "trailer_one",
  "trailer_two",
  "first_weight",
  "second_weight",
  "net_weight",
  "time_in",
  "time_out",
  "first_weight_user",
  "second_weight_user",
  "first_weight_workstation",
  "second_weight_workstation",
];

const WEIGHBRIDGE_WEIGHT_HEADER_ALIASES = {
  first_weight: [
    "first_weight",
    "tare_weight",
    "tare",
    "weight_1",
    "weight1",
    "firstWeight",
    "tareweight",
    "tare_weight_kg",
  ],
  second_weight: [
    "second_weight",
    "gross_weight",
    "gross",
    "weight_2",
    "weight2",
    "secondWeight",
    "grossweight",
    "gross_weight_kg",
  ],
  net_weight: [
    "net_weight",
    "nett_weight",
    "net",
    "nett",
    "netWeight",
    "netweight",
    "net_weight_kg",
  ],
};

const WEIGHBRIDGE_DATETIME_HEADER_ALIASES = {
  time_in: ["time_in", "weighing_time_in", "date_in", "created_on"],
  time_out: ["time_out", "weighing_time_out", "date_out"],
};

const normalizeHeaderKey = (value = "") =>
  String(value || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const parseNumericValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number(normalizedValue.replace(/,/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const parseWeightValue = (value) => parseNumericValue(value);

const formatWeightValue = (value) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  const normalizedValue = Number(value);
  return Number.isInteger(normalizedValue)
    ? String(normalizedValue)
    : String(normalizedValue);
};

const getNormalizedRowMap = (row = {}) =>
  new Map(
    Object.entries(row).map(([key, value]) => [normalizeHeaderKey(key), value]),
  );

const pickMappedValue = (rowMap, aliases = []) => {
  for (const alias of aliases) {
    const resolvedValue = rowMap.get(normalizeHeaderKey(alias));

    if (
      resolvedValue !== null &&
      resolvedValue !== undefined &&
      String(resolvedValue).trim() !== ""
    ) {
      return resolvedValue;
    }
  }

  return "";
};

const validateHeadersPresent = (headers = []) => {
  const normalizedHeaders = headers
    .map(normalizeHeaderKey)
    .filter((header) => header !== "");

  const missingHeaders = EXPECTED_WEIGHBRIDGE_COLUMN_ORDER.filter(
    (header) => !normalizedHeaders.includes(header),
  );

  if (missingHeaders.length > 0) {
    throw new Error(`Invalid headers. Missing: ${missingHeaders.join(", ")}`);
  }
};

const formatDateTimeValue = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const convertExcelSerialToDateTimeString = (value) => {
  const numericValue = parseNumericValue(value);

  if (numericValue === null) {
    return null;
  }

  const parsedDateCode = XLSX.SSF.parse_date_code(numericValue);

  if (!parsedDateCode) {
    return null;
  }

  const resolvedDate = new Date(
    Date.UTC(
      parsedDateCode.y,
      parsedDateCode.m - 1,
      parsedDateCode.d,
      parsedDateCode.H || 0,
      parsedDateCode.M || 0,
      parsedDateCode.S || 0,
    ),
  );

  return formatDateTimeValue(resolvedDate);
};

const convertDateStringToDateTimeString = (value) => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return "";
  }

  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    const exactContractMatch = normalizedValue.match(
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/,
    );

    return exactContractMatch ? normalizedValue : normalizedValue;
  }

  return formatDateTimeValue(parsedDate);
};

const normalizeDateValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return "";
  }

  const serialDateValue = convertExcelSerialToDateTimeString(value);

  if (serialDateValue) {
    return serialDateValue;
  }

  return convertDateStringToDateTimeString(normalizedValue);
};

const normalizeWeightColumns = (row = {}) => {
  const rowMap = getNormalizedRowMap(row);
  const rawFirstWeight = pickMappedValue(
    rowMap,
    WEIGHBRIDGE_WEIGHT_HEADER_ALIASES.first_weight,
  );
  const rawSecondWeight = pickMappedValue(
    rowMap,
    WEIGHBRIDGE_WEIGHT_HEADER_ALIASES.second_weight,
  );
  const rawNetWeight = pickMappedValue(
    rowMap,
    WEIGHBRIDGE_WEIGHT_HEADER_ALIASES.net_weight,
  );

  const parsedFirstWeight = parseWeightValue(rawFirstWeight);
  const parsedSecondWeight = parseWeightValue(rawSecondWeight);
  const parsedNetWeight = parseWeightValue(rawNetWeight);
  const hasAnyWeightValue =
    parsedFirstWeight !== null ||
    parsedSecondWeight !== null ||
    parsedNetWeight !== null;

  if (!hasAnyWeightValue) {
    return {
      first_weight: rawFirstWeight || "",
      second_weight: rawSecondWeight || "",
      net_weight: rawNetWeight || "",
    };
  }

  const resolvedFirstWeight = parsedFirstWeight ?? 0;
  const resolvedSecondWeight = parsedSecondWeight ?? 0;
  const resolvedNetWeight =
    parsedNetWeight ?? Math.max(resolvedSecondWeight - resolvedFirstWeight, 0);

  return {
    first_weight: formatWeightValue(resolvedFirstWeight),
    second_weight: formatWeightValue(resolvedSecondWeight),
    net_weight: formatWeightValue(resolvedNetWeight),
  };
};

const normalizeExpectedColumns = (row = {}) => {
  const rowMap = getNormalizedRowMap(row);

  return EXPECTED_WEIGHBRIDGE_COLUMN_ORDER.reduce((accumulator, header) => {
    accumulator[header] = rowMap.get(normalizeHeaderKey(header)) ?? "";
    return accumulator;
  }, {});
};

const normalizeDateColumns = (row = {}) => {
  const rowMap = getNormalizedRowMap(row);
  const rawTimeIn = pickMappedValue(
    rowMap,
    WEIGHBRIDGE_DATETIME_HEADER_ALIASES.time_in,
  );
  const rawTimeOut = pickMappedValue(
    rowMap,
    WEIGHBRIDGE_DATETIME_HEADER_ALIASES.time_out,
  );

  return {
    time_in: normalizeDateValue(rawTimeIn),
    time_out: normalizeDateValue(rawTimeOut),
  };
};

const buildNormalizedRow = (row = {}) => ({
  ...normalizeExpectedColumns(row),
  ...normalizeWeightColumns(row),
  ...normalizeDateColumns(row),
});

const buildOutputRow = (row = {}) =>
  EXPECTED_WEIGHBRIDGE_COLUMN_ORDER.map((header) => row?.[header] ?? "");

export const sanitizeWeighbridgePreviewCsvFile = async (file) => {
  if (!file) {
    return null;
  }

  const workbook = XLSX.read(await file.arrayBuffer(), {
    type: "array",
    raw: true,
  });
  const [sheetName] = workbook.SheetNames;

  if (!sheetName) {
    throw new Error("The selected CSV file is empty.");
  }

  const worksheet = workbook.Sheets[sheetName];
  const headerRows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    blankrows: false,
    raw: true,
  });
  const sourceHeaders = Array.isArray(headerRows?.[0]) ? headerRows[0] : [];

  if (!sourceHeaders.length) {
    throw new Error("The selected CSV file has no headers.");
  }

  validateHeadersPresent(sourceHeaders);

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    defval: "",
    raw: true,
  });

  const normalizedRows = rows.map((row) => buildOutputRow(buildNormalizedRow(row)));
  const outputSheet = XLSX.utils.aoa_to_sheet([
    EXPECTED_WEIGHBRIDGE_COLUMN_ORDER,
    ...normalizedRows,
  ]);
  const csvContent = XLSX.utils.sheet_to_csv(outputSheet, { blankrows: false });

  return new File([csvContent], file.name, { type: "text/csv" });
};

export { EXPECTED_WEIGHBRIDGE_COLUMN_ORDER };
